import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Calendar, User, ArrowLeft, Clock, Share2, MessageCircle, Send } from 'lucide-react'
import { useBlog } from '../hooks/useBlog'
import { useComments } from '../hooks/useComments'
import { BlogPost, CreateCommentRequest } from '../config/api'
import { Helmet } from 'react-helmet-async'

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { blog, loading, error } = useBlog(slug)
  const {
    comments,
    loading: commentsLoading,
    submitting,
    createComment,
    refresh: refreshComments,
  } = useComments(slug)

  // Comment form state
  const [commentForm, setCommentForm] = useState<CreateCommentRequest>({
    name: '',
    email: '',
    body: '',
  })
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  // Debug logging
  console.log('BlogDetail - slug:', slug)
  console.log('BlogDetail - loading:', loading)
  console.log('BlogDetail - error:', error)
  console.log('BlogDetail - blog:', blog)
  console.log('BlogDetail - comments:', comments)

  const getImageUrl = (blog: BlogPost) => {
    return (
      blog.featured_image?.url ||
      'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    )
  }

  const formatContent = (content: string) => {
    // Basic HTML rendering - you might want to use a proper markdown/HTML parser in production
    return { __html: content }
  }

  const estimateReadingTime = (content: string) => {
    const wordsPerMinute = 200
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length
    return Math.ceil(words / wordsPerMinute)
  }

  const handleShare = () => {
    if (navigator.share && blog) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: window.location.href,
      })
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href)
      // You could show a toast notification here
    }
  }

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!commentForm.name || !commentForm.email || !commentForm.body) {
      return
    }

    const success = await createComment(commentForm)

    if (success) {
      // Reset form and show success message
      setCommentForm({ name: '', email: '', body: '' })
      setShowSuccessMessage(true)
      setTimeout(() => setShowSuccessMessage(false), 5000) // Hide after 5 seconds
    }
  }

  const handleCommentChange = (field: keyof CreateCommentRequest, value: string) => {
    setCommentForm((prev) => ({ ...prev, [field]: value }))
  }

  if (loading) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-32 mb-8"></div>
            <div className="aspect-video w-full bg-gray-300 rounded-2xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-10 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="space-y-2 mt-8">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !blog) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Blog Post Not Found
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {error || 'The blog post you are looking for could not be found.'}
            </p>
            <div className="mt-8">
              <Link to="/blog" className="btn-primary">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-indojet-light py-24 sm:py-32">
      <Helmet>
        <title>{`${blog.title} | Chelsea Travel`}</title>
        <meta
          name="description"
          content={blog.excerpt || blog.description.replace(/<[^>]*>/g, '').slice(0, 160)}
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.href : '/blog'}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${blog.title} | Chelsea Travel`} />
        <meta
          property="og:description"
          content={blog.excerpt || blog.description.replace(/<[^>]*>/g, '').slice(0, 200)}
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/blog'}
        />
        <meta property="og:image" content={getImageUrl(blog)} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: blog.title,
            description: blog.excerpt || blog.description.replace(/<[^>]*>/g, '').slice(0, 300),
            image: getImageUrl(blog),
            datePublished: blog.created_at,
            author: {
              '@type': 'Person',
              name: blog.author_name,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': typeof window !== 'undefined' ? window.location.href : '',
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/blog',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: blog.title,
                item: typeof window !== 'undefined' ? window.location.href : '',
              },
            ],
          })}
        </script>
      </Helmet>
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#182c78] hover:opacity-80 font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        </div>

        {/* Hero Image */}
        <div className="relative mb-8">
          <img
            src={getImageUrl(blog)}
            alt={blog.title}
            className="aspect-video w-full rounded-2xl bg-gray-100 object-cover"
          />
          <div className="absolute top-6 left-6 bg-[#182c78] text-white px-4 py-2 rounded-full text-sm font-medium">
            {blog.category}
          </div>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
            {blog.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{blog.author_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <time dateTime={blog.created_at}>
                {new Date(blog.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </time>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{estimateReadingTime(blog.description)} min read</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-[#182c78] hover:opacity-80"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
          </div>

          {/* Excerpt */}
          {blog.excerpt && (
            <p className="text-xl leading-8 text-gray-700 mb-8 font-medium">{blog.excerpt}</p>
          )}

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <div
            dangerouslySetInnerHTML={formatContent(blog.description)}
            className="text-gray-700 leading-relaxed"
          />
        </div>

        {/* Article Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Share this article:</span>
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#182c78] text-white rounded-md hover:opacity-90 text-sm font-medium"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>

            <Link
              to="/blog"
              className="inline-flex items-center text-[#182c78] hover:opacity-80 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>

        {/* Comments Section */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-8">
            <MessageCircle className="h-6 w-6 text-[#182c78]" />
            <h3 className="text-2xl font-bold text-gray-900">Comments ({comments.length})</h3>
          </div>

          {/* Comments List */}
          <div className="space-y-6 mb-12">
            {commentsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-4 bg-gray-300 rounded w-24"></div>
                      <div className="h-3 bg-gray-300 rounded w-16"></div>
                    </div>
                    <div className="h-16 bg-gray-300 rounded"></div>
                  </div>
                ))}
              </div>
            ) : comments.length > 0 ? (
              comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#182c78] rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {comment.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{comment.name}</h4>
                        <time className="text-sm text-gray-500">
                          {new Date(comment.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </time>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{comment.body}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No comments yet. Be the first to share your thoughts!</p>
              </div>
            )}
          </div>

          {/* Comment Form */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Leave a Comment</h4>

            {showSuccessMessage && (
              <div className="mb-6 p-4 bg-green-100 border border-green-200 rounded-md">
                <p className="text-green-800 text-sm">
                  ✓ Your comment has been submitted successfully! It may take a moment to appear.
                </p>
              </div>
            )}

            <form onSubmit={handleCommentSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={commentForm.name}
                    onChange={(e) => handleCommentChange('name', e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78]"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={commentForm.email}
                    onChange={(e) => handleCommentChange('email', e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78]"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                  Comment *
                </label>
                <textarea
                  id="body"
                  value={commentForm.body}
                  onChange={(e) => handleCommentChange('body', e.target.value)}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78]"
                  placeholder="Share your thoughts about this article..."
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">Your email address will not be published.</p>
                <button
                  type="submit"
                  disabled={
                    submitting || !commentForm.name || !commentForm.email || !commentForm.body
                  }
                  className="inline-flex items-center gap-2 px-6 py-2 bg-[#182c78] text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 rounded-2xl bg-gray-50 px-6 py-12 sm:px-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              Ready to start your journey?
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Let our travel experts help you plan your perfect getaway.
            </p>
            <div className="mt-6">
              <Link to="/packages" className="btn-primary mr-4">
                Browse Packages
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
